import { Component, inject, OnInit } from '@angular/core';
import { AG_GRID_LOCALE_DE } from '../../_helpers/aggrid.locale.de';
import {
  GridApi,
  RowClassRules,
  SideBarDef,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
  SizeColumnsToContentStrategy
} from 'ag-grid-enterprise';
import { ColDef, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridThemeService } from '../../_services/ag-grid-theme.service';
import { RecordNavigationComponent } from '../_shared/record-navigation/record-navigation.component';
import { ExpectedDeliveryService } from '../../_services/expected-delivery.service';
import { ExpectedDelivery } from '../../_models/expected-delivery.interface';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'hawk-expected-deliveries-index',
  templateUrl: './expected-deliveries-index.component.html',
  standalone: true,
  imports: [
    AgGridAngular,
    RecordNavigationComponent,
    RouterLink
  ],
  host: {
    class: 'app-page'
  }
})
export class ExpectedDeliveriesIndexComponent implements OnInit {
  public hawkTheme = inject(AgGridThemeService).getTheme();
  public expectedDeliveries: ExpectedDelivery[] = [];
  protected readonly locale = AG_GRID_LOCALE_DE;
  private gridApi!: GridApi;

  public rowClassRules: RowClassRules = {
    'row-completed': (params) => params.data?.status === 'completed',
    'row-cancelled': (params) => params.data?.status === 'cancelled',
    'row-partial': (params) => params.data?.status === 'partial',
  };

  autoSizeStrategy: SizeColumnsToFitGridStrategy | SizeColumnsToFitProvidedWidthStrategy | SizeColumnsToContentStrategy = {
    type: 'fitGridWidth',
  };

  public colDefs: ColDef[] = [
    {
      field: 'order_nr',
      headerName: 'Auftrags-Nr.',
      width: 150,
      cellRenderer: this.orderNrRenderer,
    },
    {
      field: 'supplier_name',
      headerName: 'Lieferant',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      cellRenderer: this.statusRenderer,
    },
    {
      field: 'project_name',
      headerName: 'Projekt',
      width: 180,
    },
    {
      field: 'items_count',
      headerName: 'Positionen',
      width: 100,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
    },
    {
      field: 'total_ordered',
      headerName: 'Soll',
      width: 80,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
    },
    {
      field: 'total_received',
      headerName: 'Ist',
      width: 80,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
    },
    {
      field: 'progress_percentage',
      headerName: 'Fortschritt',
      width: 120,
      cellRenderer: this.progressRenderer,
    },
    {
      field: 'expected_until',
      headerName: 'Erwartet bis',
      width: 120,
      valueFormatter: this.dateFormatter,
    },
    {
      field: 'created_by_name',
      headerName: 'Erstellt von',
      width: 150,
    },
    {
      field: 'created_at',
      headerName: 'Erstellt am',
      width: 120,
      valueFormatter: this.dateFormatter,
    },
  ];

  public sideBarOptions: SideBarDef = {
    hiddenByDefault: true,
    toolPanels: ['columns', 'filters']
  };

  public defaultColDef: ColDef = {
    filter: true,
    sortable: true,
    resizable: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: false,
  };

  public rowSelection: 'single' | 'multiple' = 'single';

  constructor(private expectedDeliveryService: ExpectedDeliveryService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.expectedDeliveryService.getAll().subscribe({
      next: (data) => {
        this.expectedDeliveries = data;
      },
      error: (err) => {
        console.error('Fehler beim Laden der erwarteten Lieferungen:', err);
      }
    });
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  public quickSearchChanged(e: Event): void {
    const newValue = (e.target as HTMLInputElement).value;
    this.gridApi.setGridOption('quickFilterText', newValue);
  }

  orderNrRenderer(params: any): string {
    const id = params.data?.id;
    const orderNr = params.value || '-';
    return `<a class="bm-link bm-link-hover font-medium" href="/a/lager/erwartete-lieferungen/${id}" rel="noopener">${orderNr}</a>`;
  }

  statusRenderer(params: any): string {
    const status = params.value;
    const statusMap: Record<string, { label: string; class: string }> = {
      'open': { label: 'Offen', class: 'badge-info' },
      'partial': { label: 'Teilweise', class: 'badge-warning' },
      'completed': { label: 'Abgeschlossen', class: 'badge-success' },
      'cancelled': { label: 'Storniert', class: 'badge-error' },
    };
    const statusInfo = statusMap[status] || { label: status, class: 'badge-ghost' };
    return `<span class="badge ${statusInfo.class} badge-sm">${statusInfo.label}</span>`;
  }

  progressRenderer(params: any): string {
    const percentage = params.value || 0;
    const colorClass = percentage >= 100 ? 'progress-success' : percentage > 0 ? 'progress-warning' : 'progress-info';
    return `
      <div class="flex items-center gap-2">
        <progress class="progress ${colorClass} w-16 h-2" value="${percentage}" max="100"></progress>
        <span class="text-xs">${percentage}%</span>
      </div>
    `;
  }

  dateFormatter(params: ValueFormatterParams): string {
    if (!params.value) return '';
    const date = new Date(params.value);
    return date.toLocaleDateString('de-DE');
  }
}
